import { useEffect, useMemo, useState } from "react";
import { NOTIFY_STATUSES, STATUS_LABEL, STATUS_NOTIFY_PREVIEW } from "../constants.ts";
import type { MailPreview, NotifyTemplate, Ticket } from "../types.ts";
import { ticketContactEmails } from "../utils/ticketDisplay.ts";
import { hasSalutation, setSalutation } from "../utils/mailBody.ts";
import { apiWithRetry } from "../api.ts";

export type StatusChangeConfirm = {
  notify: boolean;
  notifyEmails: string[];
  notifyBody?: string;
};

export function StatusChangeModal({
  ticket,
  newStatus,
  templates,
  onClose,
  onConfirm,
}: {
  ticket: Ticket;
  newStatus: string;
  templates: NotifyTemplate[];
  onClose: () => void;
  onConfirm: (opts: StatusChangeConfirm) => Promise<void>;
}) {
  const emails = useMemo(() => ticketContactEmails(ticket), [ticket]);
  const canNotify = NOTIFY_STATUSES.has(newStatus) && emails.length > 0;
  const tpl = templates.find((x) => x.status === newStatus);
  const defaultBody = tpl?.body || STATUS_NOTIFY_PREVIEW[newStatus] || "";
  const subjectSuffix = tpl?.subject_suffix || STATUS_NOTIFY_PREVIEW[newStatus] || "";

  const [notify, setNotify] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(() => new Set(emails));
  const [body, setBody] = useState(defaultBody);
  const [submitting, setSubmitting] = useState(false);
  const [rendered, setRendered] = useState<MailPreview | null>(null);
  const [renderErr, setRenderErr] = useState("");

  // 実際に届くメールを BE のレンダラから取る（送信と同じ組み立て）。
  // 旧実装は「冒頭に〇〇様、末尾に署名が付きます」という手書きの注記だけで、
  // 本文を上書きすると宛名が消えることが画面から分からなかった。
  useEffect(() => {
    if (!notify) return;
    let alive = true;
    const timer = window.setTimeout(() => {
      apiWithRetry<MailPreview>(`/support/tickets/${ticket.id}/mail-preview`, {
        method: "POST",
        body: JSON.stringify({
          kind: `status:${newStatus}`,
          body,
          to_addrs: Array.from(selectedEmails),
        }),
      })
        .then((d) => { if (alive) { setRendered(d); setRenderErr(""); } })
        .catch((e) => { if (alive) setRenderErr(e instanceof Error ? e.message : String(e)); });
    }, 400);
    return () => { alive = false; window.clearTimeout(timer); };
  }, [notify, body, selectedEmails, ticket.id, newStatus]);

  // 宛名は本文先頭の {{customer_salutation}} で出す。チェックはその付け外しに落とす。
  const salutationOn = hasSalutation(body);

  const toggleEmail = (email: string) => {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const canSubmit = !notify || (selectedEmails.size > 0 && body.trim().length > 0);

  const handleConfirm = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm({
        notify: notify && canNotify,
        notifyEmails: notify ? Array.from(selectedEmails) : [],
        ...(notify && canNotify ? { notifyBody: body.trim() } : {}),
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const statusLabel = STATUS_LABEL[newStatus] || newStatus;

  return (
    <div
      className="support-modal-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="support-modal support-status-change-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="status-change-title"
      >
        <div className="support-modal__header">
          <h3 id="status-change-title" className="support-modal__title">
            ステータスを「{statusLabel}」に変更
          </h3>
          <button type="button" className="support-modal__close" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>

        {canNotify ? (
          <>
            <fieldset className="support-status-change-modal__notify-mode">
              <legend className="support-status-change-modal__legend">顧客への通知</legend>
              <label className="support-status-change-modal__radio">
                <input
                  type="radio"
                  name="notify-mode"
                  checked={!notify}
                  onChange={() => setNotify(false)}
                />
                通知しない（ステータスのみ変更）
              </label>
              <label className="support-status-change-modal__radio">
                <input
                  type="radio"
                  name="notify-mode"
                  checked={notify}
                  onChange={() => setNotify(true)}
                />
                顧客へ通知メールを送る
              </label>
            </fieldset>

            {notify && (
              <>
                <div className="support-status-change-modal__section">
                  <div className="support-status-change-modal__label">送信先（複数選択可）</div>
                  <div className="support-status-change-modal__recipients">
                    {emails.map((email) => (
                      <label key={email} className="support-status-change-modal__recipient">
                        <input
                          type="checkbox"
                          checked={selectedEmails.has(email)}
                          onChange={() => toggleEmail(email)}
                        />
                        <span className="mono">{email}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="support-status-change-modal__meta">
                  <span className="support-status-change-modal__meta-label">件名</span>
                  <span>{rendered ? rendered.subject : `[${ticket.ticket_number}] ${subjectSuffix}`}</span>
                </div>

                <div className="support-status-change-modal__section">
                  <div className="support-status-change-modal__label">
                    本文（送信前に編集できます）
                    <label
                      className="support-status-change-modal__salutation"
                      title="メールの一番上に「〇〇 様」を入れます（連絡担当者名が空なら「ご担当者様」）"
                    >
                      <input
                        type="checkbox"
                        checked={salutationOn}
                        onChange={(e) => setBody(setSalutation(body, e.target.checked))}
                      />
                      宛名を入れる
                      {rendered?.salutation && (
                        <span className="support-status-change-modal__salutation-value">
                          （{rendered.salutation.trim()}）
                        </span>
                      )}
                    </label>
                  </div>
                  <textarea
                    className="support-status-change-modal__body"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={7}
                    autoFocus
                  />
                </div>

                <div className="support-status-change-modal__section">
                  <div className="support-status-change-modal__label">
                    実際に届くメール{rendered ? "" : "（読み込み中…）"}
                  </div>
                  {renderErr ? (
                    <div className="support-status-change-modal__note">
                      プレビューを取得できませんでした: {renderErr}
                    </div>
                  ) : (
                    <div className="support-status-change-modal__rendered">
                      <span className="support-status-change-modal__rendered-salutation">{rendered?.salutation}</span>
                      <span className="support-status-change-modal__rendered-main">{rendered?.main}</span>
                      <span className="support-status-change-modal__rendered-footer">{rendered?.footer}</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          <p className="support-status-change-modal__simple">
            {NOTIFY_STATUSES.has(newStatus) && emails.length === 0
              ? "連絡先メールが未登録のため、通知は送信できません。ステータスのみ変更します。"
              : `「${statusLabel}」に変更します。`}
          </p>
        )}

        <div className="support-modal__actions">
          <button type="button" className="support-btn-outline" onClick={onClose}>
            キャンセル
          </button>
          <button
            type="button"
            className="support-btn-primary"
            onClick={() => void handleConfirm()}
            disabled={!canSubmit || submitting}
          >
            {submitting
              ? "処理中…"
              : notify && canNotify
                ? `「${statusLabel}」に変更して送信`
                : `「${statusLabel}」に変更`}
          </button>
        </div>
      </div>
    </div>
  );
}
